<script setup lang="ts">
import { computed } from "vue";
import Icon from "./Icon.vue";
import type { Progress } from "../api";

const props = defineProps<{ progress: Progress | null; running: boolean }>();

// Etapy w kolejności — pasek pokazuje, gdzie w całym procesie jesteśmy, a nie
// tylko procent bieżącego kroku. Analiza to etap najdłuższy i jedyny, w którym
// „12/600 plików" coś znaczy, więc tam pokazujemy licznik.
const STAGES: Progress["stage"][] = ["connect", "walk", "clamav", "analyze", "actions", "health", "system", "report", "done"];

const stageIdx = computed(() => (props.progress ? STAGES.indexOf(props.progress.stage) : 0));
const pct = computed(() => {
  const p = props.progress;
  if (!p) return 0;
  if (p.stage === "done") return 100;
  const base = (stageIdx.value / (STAGES.length - 1)) * 100;
  const span = 100 / (STAGES.length - 1);
  const inner = p.total && p.total > 0 && p.done !== null ? Math.min(1, p.done / p.total) : 0;
  return Math.min(99, Math.round(base + span * inner));
});
const threats = computed(() => {
  const t = props.progress?.threats;
  return t ? t.backdoor + t.injected + t.suspicious : 0;
});
const counter = computed(() => {
  const p = props.progress;
  if (!p || p.total === null || p.done === null) return "";
  return `${p.done} / ${p.total}`;
});
</script>

<template>
  <div v-if="progress" class="prog">
    <div class="top">
      <span class="label">
        <span v-if="running" class="dot" />
        {{ progress.label }}
        <span v-if="counter" class="muted mono">{{ counter }}</span>
      </span>
      <span class="muted">{{ progress.files.toLocaleString("pl") }} plików • {{ progress.candidates }} kandydatów</span>
    </div>
    <div class="bar"><div class="fill" :style="{ width: pct + '%' }" /></div>

    <div class="stats">
      <div class="stat" :class="{ hot: threats > 0 }">
        <div class="n">{{ threats }}</div>
        <div class="l">zagrożeń</div>
      </div>
      <div class="stat crit"><div class="n">{{ progress.threats.backdoor }}</div><div class="l">backdoory</div></div>
      <div class="stat warn"><div class="n">{{ progress.threats.injected }}</div><div class="l">wstrzyknięcia</div></div>
      <div class="stat warn2"><div class="n">{{ progress.threats.suspicious }}</div><div class="l">podejrzane</div></div>
      <div class="stat ok"><div class="n">{{ progress.actions.quarantined }}</div><div class="l">kwarantanna</div></div>
      <div class="stat ok"><div class="n">{{ progress.actions.repaired }}</div><div class="l">naprawione</div></div>
    </div>

    <div class="foot muted">
      <span v-if="progress.withoutAi"><Icon name="zap" /> {{ progress.withoutAi }} plików ocenionych bez AI (reguły, kopie)</span>
      <span v-if="progress.costUsd > 0"><Icon name="coins" /> koszt AI ~${{ progress.costUsd.toFixed(2) }}</span>
      <span v-if="progress.siteOk === true" class="okc"><Icon name="check" /> strona odpowiada poprawnie</span>
      <span v-if="progress.siteOk === false" class="critc"><Icon name="x" /> strona nadal nie odpowiada poprawnie</span>
    </div>
  </div>
</template>

<style scoped>
.prog { margin: 0 0 var(--s3); }
.top { display: flex; justify-content: space-between; gap: var(--s3); flex-wrap: wrap; font-size: 12px; margin-bottom: var(--s2); }
.label { font-weight: 600; display: inline-flex; align-items: center; gap: var(--s2); }
.dot { width: 8px; height: 8px; border-radius: 50%; background: var(--acc); animation: pulse 1.2s infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
.bar { height: 6px; background: var(--panel3); border-radius: 99px; overflow: hidden; }
.fill { height: 100%; background: var(--acc); transition: width .4s ease; }
.stats { display: grid; grid-template-columns: repeat(6, 1fr); gap: var(--s2); margin-top: var(--s3); }
@media (max-width: 560px) { .stats { grid-template-columns: repeat(3, 1fr); } }
.stat { background: var(--panel2); border: 1px solid var(--bd); border-radius: var(--r2); padding: var(--s2); text-align: center; }
.stat .n { font-size: 18px; font-weight: 700; line-height: 1.1; }
.stat .l { font-size: 10px; color: var(--mut2); text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }
.stat.hot .n { color: var(--crit); }
.stat.crit .n { color: var(--crit); }
.stat.warn .n, .stat.warn2 .n { color: var(--warn); }
.stat.ok .n { color: var(--ok); }
.foot { display: flex; gap: var(--s4); flex-wrap: wrap; font-size: 12px; margin-top: var(--s2); }
.okc { color: var(--ok); } .critc { color: var(--crit); font-weight: 600; }
</style>
